import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { darken } from 'polished';
import { connect } from 'react-redux';

const Canvas = styled.canvas`
   border-radius: 10px;
   image-rendering: crisp-edges;
   image-rendering: pixelated;
   width: calc(100% + 10px);
   position: relative;
   left: -5px;
   top: 2px;
   height: 41px;
   background: ${darken(0.075, '#d1e8e8')};
   padding: 2px 0;

   @media (prefers-color-scheme: dark) {
      & {
         background: ${darken(0.02, '#003536')};
      }
   }
`;

const CanvasOverlay = styled.div`
   border-radius: 10px;
   border: 2px solid var(--text-color);
   position: absolute;
   top: 0;
   right: 0;
   bottom: 0;
   left: 0;
   cursor: pointer;

   &:hover {
      border-color: var(--brand-dark-orange);
   }
`;

const CanvasWrapper = styled.div`
   height: 45px;
   width: calc(100% - 10px);
   overflow: hidden;
   position: relative;
   margin: 5px;
   border-radius: 10px;
`;

const answers = {
   correct: 'correct',
   missed: 'missed',
   incorrectLength: 'incorrectLength',
};

const createNoteId = (note, isReference) =>
   `${note.startPart}-${note.note}${isReference ? '-r' : ''}`;

const mapStateToProps = ({
   activeInterface: { referencePlaying, userPlaying },
}) => ({
   referencePlaying,
   userPlaying,
});

const MiniPianoRollDisplay = connect(mapStateToProps)(
   ({
      referencePlaying,
      userPlaying,
      pattern,
      referencePattern,
      compareAnswer,
      onClick,
   }) => {
      const [canvasDimensions, setCanvasDimensions] = useState({
         width: null,
         height: null,
      });
      const canvasRef = useRef();

      useEffect(() => {
         setCanvasDimensions({
            width: canvasRef.current.clientWidth - 10,
            height: canvasRef.current.clientHeight - 4,
         });
         // need to update dimensions if pattern changes size
      }, [pattern.length]);

      useEffect(() => {
         if (canvasDimensions.width == null) {
            return;
         }

         const darkMode = window.matchMedia(
            '(prefers-color-scheme: dark)'
         ).matches;

         const normalColor = '#00898a';
         const correctColor = darkMode ? '#00a352' : '#00cc66';
         const missingColor = darkMode ? '#ffc800' : '#e6a100';
         const incorrectColor = darkMode ? '#c51b29' : '#e43a48';

         const canvasContext = canvasRef.current.getContext('2d');
         canvasContext.clearRect(
            0,
            0,
            canvasDimensions.width,
            canvasDimensions.height
         );

         let processedNotes;
         if (!compareAnswer) {
            processedNotes = pattern.flat();
         } else {
            const noteMap = {};
            pattern.flat().forEach((note) => {
               noteMap[createNoteId(note)] = { ...note };
            });
            referencePattern.flat().forEach((note) => {
               const noteId = createNoteId(note);
               if (noteMap[noteId]?.noteLength === note.noteLength) {
                  noteMap[noteId].miniPrComparedAnswer = answers.correct;
               } else if (noteMap[noteId] == null) {
                  noteMap[noteId] = {
                     ...note,
                     miniPrComparedAnswer: answers.missed,
                  };
               } else {
                  noteMap[noteId].miniPrComparedAnswer =
                     answers.incorrectLength;

                  noteMap[noteId].correctLength = note.noteLength;
               }
            });
            processedNotes = Object.values(noteMap);
         }

         let lowestNote = processedNotes[0]?.note;
         let highestNote = processedNotes[0]?.note;
         processedNotes.forEach(({ note }) => {
            if (note < lowestNote) {
               lowestNote = note;
            } else if (note > highestNote) {
               highestNote = note;
            }
         });

         const noteRange = highestNote - lowestNote + 1;
         const noteHeight = canvasDimensions.height / Math.max(5, noteRange);
         const noteOffset =
            noteRange < 5 ? ((5 - noteRange) / 2) * noteHeight : 0;
         const partWidth = canvasDimensions.width / pattern.length;

         canvasContext.fillStyle = normalColor;
         processedNotes.forEach(
            ({
               note,
               startPart,
               noteLength,
               correctLength,
               miniPrComparedAnswer,
            }) => {
               if (
                  !compareAnswer ||
                  !(referencePlaying || userPlaying) ||
                  (referencePlaying && miniPrComparedAnswer != null) ||
                  (userPlaying && miniPrComparedAnswer !== answers.missed)
               ) {
                  if (miniPrComparedAnswer === answers.incorrectLength) {
                     if (!userPlaying) {
                        canvasContext.fillStyle = missingColor;

                        canvasContext.fillRect(
                           startPart * partWidth,
                           (note - highestNote) * -1 * noteHeight + noteOffset,
                           correctLength * partWidth,
                           noteHeight
                        );
                     }
                  }

                  if (compareAnswer) {
                     switch (miniPrComparedAnswer) {
                        case answers.correct: {
                           canvasContext.fillStyle = correctColor;
                           break;
                        }
                        case answers.missed: {
                           canvasContext.fillStyle = missingColor;
                           break;
                        }
                        default: {
                           canvasContext.fillStyle = incorrectColor;
                        }
                     }
                  }

                  if (
                     !(
                        miniPrComparedAnswer === answers.incorrectLength &&
                        referencePlaying
                     )
                  ) {
                     canvasContext.fillRect(
                        startPart * partWidth,
                        (note - highestNote) * -1 * noteHeight + noteOffset,
                        noteLength * partWidth,
                        noteHeight
                     );
                  }
               }
            }
         );
      }, [
         pattern,
         referencePattern,
         canvasDimensions,
         compareAnswer,
         compareAnswer && referencePlaying,
         compareAnswer && userPlaying,
      ]);

      return (
         <CanvasWrapper onClick={onClick}>
            <Canvas
               ref={canvasRef}
               width={canvasDimensions.width}
               height={canvasDimensions.height}
            />
            <CanvasOverlay />
         </CanvasWrapper>
      );
   }
);

export { MiniPianoRollDisplay };
